import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import type {
  Affordability,
  AffordSource,
  HouseholdSettings,
  CalendarEvent,
  CardOption,
  CardOverview,
  CategorySpend,
  CategoryTrend,
  Dashboard,
  DebtPoint,
  Owner,
  PeriodReport,
  PeriodSpend,
  Reconciliation,
} from "../types";
import { keys } from "./keys";

// ---------------------------------------------------------------- dashboard & views

export const dashboardQuery = () =>
  queryOptions({
    queryKey: keys.dashboard,
    queryFn: () => api.get<Dashboard>("/dashboard"),
    // the one screen that refreshes when you come back to the app (the rest wait for a visit)
    refetchOnWindowFocus: true,
  });

export const useDashboard = () => useQuery(dashboardQuery());

export const cardsQuery = () =>
  queryOptions({ queryKey: keys.cards, queryFn: () => api.get<CardOverview[]>("/cards") });

export const useCards = () => useQuery(cardsQuery());

/** ¿Cuadra con el banco? (#46): each card's latest statement with the bank's saldo al corte. */
export const useReconciliation = () =>
  useQuery({
    queryKey: keys.reconciliation,
    queryFn: () => api.get<Reconciliation[]>("/cards/reconciliation"),
  });

/** ¿Con qué tarjeta pago? (#43): the cards a purchase made today could go on, best first.
 *  `owner`: whose purchase it is (BOTH: joint cards only); without it, every card. */
export const useBestCard = (
  amount: string | null,
  msi?: number,
  owner?: Owner,
  { enabled = true }: { enabled?: boolean } = {},
) =>
  useQuery({
    queryKey: keys.bestCard(amount ?? "0", msi, owner),
    queryFn: () =>
      api.get<CardOption[]>("/cards/best", { amount: amount ?? "0", msi_months: msi, owner }),
    enabled,
    placeholderData: (prev) => prev,
  });

export const useCalendar = (start: string, end: string) =>
  useQuery({
    queryKey: keys.calendar(start, end),
    queryFn: () => api.get<CalendarEvent[]>("/calendar", { start, end }),
    placeholderData: (prev) => prev,
  });

export const useSpendingByCategory = (period?: string, owner?: Owner) =>
  useQuery({
    queryKey: keys.byCategory(period, owner),
    queryFn: () => api.get<CategorySpend[]>("/charts/by-category", { period, owner }),
    placeholderData: (prev) => prev,
  });

export const useSpendingByPeriod = (count = 12) =>
  useQuery({
    queryKey: keys.byPeriod(count),
    queryFn: () => api.get<PeriodSpend[]>("/charts/by-period", { count }),
  });

export const useCategoryTrend = (count = 12) =>
  useQuery({
    queryKey: keys.categoryTrend(count),
    queryFn: () => api.get<CategoryTrend>("/charts/category-trend", { count }),
  });

/** Cierre del periodo (#50); without a key, the periodo that closed last. */
export const usePeriodReport = (period?: string) =>
  useQuery({
    queryKey: keys.periodReport(period),
    queryFn: () => api.get<PeriodReport>("/reports/period", { period }),
    placeholderData: (prev) => prev,
  });

export const useDebtChart = () =>
  useQuery({ queryKey: keys.debtChart, queryFn: () => api.get<DebtPoint[]>("/charts/debt") });

export const useAffordability = (
  price: string | null,
  msi?: number,
  source: AffordSource = "general",
  categoryId?: number,
  accountId?: number,
) =>
  useQuery({
    queryKey: keys.affordability(price ?? "", msi, source, categoryId, accountId),
    queryFn: () =>
      api.get<Affordability>("/affordability", {
        price,
        msi_months: msi,
        source,
        category_id: source === "category" ? categoryId : undefined,
        account_id: accountId,
      }),
    enabled: !!price && Number(price) > 0 && (source !== "category" || categoryId != null),
    placeholderData: (prev) => prev,
  });

export const useHouseholdSettings = () =>
  useQuery({ queryKey: keys.settings, queryFn: () => api.get<HouseholdSettings>("/settings") });

export function useSaveHouseholdSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<HouseholdSettings>) =>
      api.patch<HouseholdSettings>("/settings", body),
    onSuccess: async (settings) => {
      qc.setQueryData(keys.settings, settings);
      await qc.invalidateQueries({ queryKey: ["affordability"] });
    },
  });
}
